import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTaller3D } from '../../context/Taller3DContext';
import { useClientes } from '../../context/ClientesContext';
import { EstadoPresupuesto3D } from '../../types';
import { formatearImporte, calcularPedido3D, resumenTexto } from '../../utils/calculo3d';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Plus, Trash2, Copy, FileText, Package, User as UserIcon } from 'lucide-react';
import { copiarTexto } from '../../utils/plataforma';
import { useConfirmar } from '../../context/ConfirmarContext';

const ESTADOS: { valor: EstadoPresupuesto3D; label: string; clase: string }[] = [
  { valor: 'borrador',  label: 'Borrador',  clase: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  { valor: 'enviado',   label: 'Enviado',   clase: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  { valor: 'aceptado',  label: 'Aceptado',  clase: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  { valor: 'rechazado', label: 'Rechazado', clase: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
];

export const Presupuestos3D: React.FC = () => {
  const confirmar = useConfirmar();
  const { presupuestos3D, loading3D, eliminarPresupuesto3D, actualizarPresupuesto3D } = useTaller3D();
  const { clientes, clientePorId } = useClientes();
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState<'todos' | EstadoPresupuesto3D>('todos');
  const [filtroCliente, setFiltroCliente] = useState('');

  const lista = useMemo(
    () => presupuestos3D
      .filter(x => filtro === 'todos' || x.estado === filtro)
      .filter(x => !filtroCliente || x.clienteId === filtroCliente),
    [presupuestos3D, filtro, filtroCliente],
  );

  const totalAceptado = useMemo(
    () => presupuestos3D
      .filter(x => x.estado === 'aceptado')
      .reduce((s, x) => s + (x.resumen?.precioTotalConIva ?? 0), 0),
    [presupuestos3D],
  );

  const borrar = async (id: string, nombre: string) => {
    if (!(await confirmar({ titulo: `¿Borrar el presupuesto «${nombre}»?`, accion: 'Borrar' }))) return;
    await eliminarPresupuesto3D(id);
  };

  const copiar = async (id: string) => {
    const pre = presupuestos3D.find(x => x.id === id);
    if (!pre) return;
    const texto = resumenTexto(pre.parametros, calcularPedido3D(pre.parametros));
    try { await copiarTexto(texto); } catch { /* sin portapapeles */ }
  };

  if (loading3D) {
    return <div className="max-w-5xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">

      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Presupuestos 3D</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {presupuestos3D.length} guardado{presupuestos3D.length === 1 ? '' : 's'}
            {totalAceptado > 0 && ` · ${formatearImporte(totalAceptado, '€')} aceptados`}
          </p>
        </div>
        <Link to="/taller3d/calculadora"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                     bg-orange-600 hover:bg-orange-700 text-white transition">
          <Plus className="w-4 h-4" /> Nuevo presupuesto
        </Link>
      </header>

      {/* Filtro por estado */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {([{ valor: 'todos', label: 'Todos' }, ...ESTADOS] as { valor: string; label: string }[]).map(e => (
          <button key={e.valor} onClick={() => setFiltro(e.valor as any)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
              filtro === e.valor
                ? 'bg-gray-900 dark:bg-orange-600 border-gray-900 dark:border-orange-600 text-white'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
            }`}>
            {e.label}
          </button>
        ))}
        {clientes.length > 0 && (
          <select value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}
            className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-full border
                       border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800
                       text-gray-600 dark:text-gray-300 cursor-pointer">
            <option value="">Todos los clientes</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        )}
      </div>

      {lista.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <Package className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            {presupuestos3D.length === 0
              ? 'Todavía no has guardado ningún presupuesto.'
              : 'Ningún presupuesto con ese estado.'}
          </p>
          {presupuestos3D.length === 0 && (
            <Link to="/taller3d/calculadora"
              className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-semibold rounded-lg
                         bg-orange-600 hover:bg-orange-700 text-white transition">
              <Plus className="w-4 h-4" /> Calcular el primero
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {lista.map(pre => {
            const moneda = pre.parametros?.moneda ?? '€';
            const res = pre.resumen ?? { lineas: 0, unidades: 0, costeTotal: 0, precioTotalConIva: 0, beneficioTotal: 0 };
            const articulos = pre.parametros?.lineas ?? [];
            const materiales = Array.from(new Set(articulos.map(l => l.material).filter(Boolean)));
            return (
              <div key={pre.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4
                           hover:border-gray-300 dark:hover:border-gray-600 transition">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <button onClick={() => navigate(`/taller3d/calculadora?id=${pre.id}`)}
                    className="text-left min-w-0 flex-1">
                    <h2 className="font-bold text-gray-900 dark:text-white truncate">{pre.nombre}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {articulos.length} artículo{articulos.length === 1 ? '' : 's'} ·{' '}
                      {res.unidades} ud{res.unidades === 1 ? '' : 's'} ·{' '}
                      {materiales.length ? materiales.join(' + ') : '—'} ·{' '}
                      {format(pre.updatedAt, "d 'de' MMMM yyyy", { locale: es })}
                    </p>
                    {pre.clienteId && (
                      <p className="inline-flex items-center gap-1 text-xs font-medium
                                    text-purple-600 dark:text-purple-400 mt-1">
                        <UserIcon className="w-3 h-3" />
                        {clientePorId(pre.clienteId)?.nombre ?? 'Cliente borrado'}
                      </p>
                    )}
                    {articulos.length > 1 && (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 truncate">
                        {articulos.map(l => `${l.nombre || 'Sin nombre'} ×${l.unidades}`).join(' · ')}
                      </p>
                    )}
                  </button>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <select value={pre.estado}
                      onChange={e => actualizarPresupuesto3D(pre.id, { estado: e.target.value as EstadoPresupuesto3D })}
                      className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 cursor-pointer
                                  ${ESTADOS.find(e2 => e2.valor === pre.estado)?.clase ?? ESTADOS[0].clase}`}>
                      {ESTADOS.map(e => <option key={e.valor} value={e.valor}>{e.label}</option>)}
                    </select>
                    <button onClick={() => copiar(pre.id)} title="Copiar resumen"
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={() => borrar(pre.id, pre.nombre)} title="Borrar"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                  {[
                    ['Coste total', formatearImporte(res.costeTotal, moneda)],
                    ['Total con IVA', formatearImporte(res.precioTotalConIva, moneda)],
                    ['Coste medio / ud', formatearImporte(res.unidades > 0 ? res.costeTotal / res.unidades : 0, moneda)],
                    ['Beneficio', formatearImporte(res.beneficioTotal, moneda)],
                  ].map(([k, v], i) => (
                    <div key={k}>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">{k}</p>
                      <p className={`font-mono text-sm tabular-nums ${
                        i === 3
                          ? res.beneficioTotal < 0
                            ? 'text-red-600 dark:text-red-400 font-semibold'
                            : 'text-green-600 dark:text-green-400 font-semibold'
                          : 'text-gray-900 dark:text-white'
                      }`}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="flex items-start gap-2 text-[11px] text-gray-500 dark:text-gray-500 mt-6">
        <FileText className="w-3.5 h-3.5 shrink-0 mt-px" />
        Marcar un presupuesto como aceptado será, más adelante, el disparador para generar el pedido
        y su factura desde estos mismos datos.
      </p>
    </div>
  );
};
